import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { onRequest, onResponse, onResponseError } from '@/lib/circuitBreaker';
import { getStore } from '@/store/storeRef';
import { localeHref } from "@/i18n/localeHref";

// IMPORTANT — import cycle avoidance.
// apiClient must NOT import `@/store` or `@/store/userSlice` directly:
//   apiClient → store/index → userSlice → apiClient
// is a cycle, and `store/index` uses the slice reducers at
// module-eval time (configureStore), so the cycle deadlocks with
// "Cannot access '{default export}' before initialization".
// Instead we reach the store lazily via `getStore()` (storeRef.ts is
// outside the cycle) and dispatch a plain action object built by hand
// — see USER_SET_BILLING_STATUS below.

// Create axios instance
const apiClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  timeout: 600000,
});

// ── In-flight GET dedup ────────────────────────────────────────────
// Coalesces concurrent identical GETs into a single underlying request.
// Stored on globalThis: Turbopack/HMR sometimes loads `apiClient` as
// multiple module instances across the server/client/dynamic chunk graph,
// and a per-instance Map fails to dedupe across them — every callsite
// would see an empty Map and fire a fresh request.
const __INFLIGHT_GETS_KEY = '__refinea_inflight_gets' as const;
type GlobalWithGets = typeof globalThis & {
  [__INFLIGHT_GETS_KEY]?: Map<string, Promise<AxiosResponse>>;
};
const _g = globalThis as GlobalWithGets;
const inflightGets: Map<string, Promise<AxiosResponse>> =
  _g[__INFLIGHT_GETS_KEY] ?? (_g[__INFLIGHT_GETS_KEY] = new Map());

function dedupKey(config: AxiosRequestConfig): string {
  const params = config.params
    ? JSON.stringify(
        Object.keys(config.params as Record<string, unknown>)
          .sort()
          .reduce<Record<string, unknown>>((acc, k) => {
            acc[k] = (config.params as Record<string, unknown>)[k];
            return acc;
          }, {}),
      )
    : '';
  return `${(config.method || 'get').toLowerCase()} ${config.url} ${params}`;
}

const rawGet = apiClient.get.bind(apiClient);
apiClient.get = ((url: string, config?: AxiosRequestConfig) => {
  // Caller-owned AbortSignal: bypass dedup. Sharing a promise across mounts
  // means the first abort kills it for everyone — including a second mount
  // that would otherwise have produced fresh data (Strict Mode, fast nav).
  if (config?.signal) return rawGet(url, config);
  const key = dedupKey({ method: 'get', url, params: config?.params });
  const existing = inflightGets.get(key);
  if (existing) return existing as Promise<AxiosResponse>;
  const p = rawGet(url, config).finally(() => {
    inflightGets.delete(key);
  });
  inflightGets.set(key, p);
  return p;
}) as typeof apiClient.get;

// ── Interceptor ordering ─────────────────────────────────────────
//
// axios execution order:
//   - REQUEST interceptors run LIFO (last-registered runs first).
//   - RESPONSE interceptors run FIFO (first-registered runs first).
// Source: axios/lib/core/Axios.js (it `unshift`s onto the request
// chain and `push`es onto the response chain).
//
// We want:
//   1. Request side — breaker decides BEFORE the auth header is added,
//      so an OPEN circuit short-circuits without burning a token slot.
//      → Register breaker LAST on the request side (LIFO → runs first).
//   2. Response side — the 401 handler runs FIRST (decides on redirect),
//      then the breaker counts the failure for circuit health. This
//      lets us count 401s as health signals without disrupting the
//      auth flow, and means redirected requests still age the breaker.
//      → Register 401 handler FIRST on the response side, breaker
//        SECOND (FIFO → 401 runs first).

// Response interceptor #1 — 401 redirect + 402 paywall handler. Runs first.
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // ── 402 Payment Required — the BillingGateMiddleware paywall ──
    // The backend has positively determined the user is locked out
    // (trial expired / canceled / past_due). The server is the source
    // of truth on billing, so we sync that into Redux immediately:
    // writing the locked status flips the BlurGate overlay on at once,
    // instead of leaving the user looking at scattered 402 errors
    // until the next /auth/me refresh. We do NOT redirect — the
    // BlurGate renders in place over the current route.
    // A never-paid account calling paid work (`payment_required`) is not
    // locked out: it belongs on the onboarding paywall, not under the
    // "your trial ended" overlay. Let the caller handle it.
    if (error.response?.status === 402 && error.response?.data?.detail?.code === "payment_required") {
      return Promise.reject(error);
    }
    if (error.response?.status === 402) {
      // The server sends `billing_status` in the 402 body. Accept it
      // only if it's one of the known locked statuses; otherwise fall
      // back to a generic locked status so the gate still engages.
      const raw = error.response?.data?.billing_status;
      const LOCKED = ["past_due", "canceled", "pro_trial_expired"] as const;
      type Locked = (typeof LOCKED)[number];
      const lockedStatus: Locked =
        typeof raw === "string" && (LOCKED as readonly string[]).includes(raw)
          ? (raw as Locked)
          : "pro_trial_expired";
      try {
        const s = getStore();
        if (s) {
          // Plain action object equivalent to
          // userSlice.actions.setBillingStatus(lockedStatus). RTK
          // derives the type as `<sliceName>/<reducerName>` — the
          // userSlice is created with name:'user', reducer
          // `setBillingStatus`. Building it by hand keeps apiClient
          // out of the import cycle.
          s.dispatch({ type: "user/setBillingStatus", payload: lockedStatus });
        }
      } catch {
        // Dispatch should never throw; guard anyway so a billing
        // edge case can't break the whole interceptor chain.
      }
      return Promise.reject(error);
    }

    // If we get a 401, remove the token and redirect to login
    if (error.response?.status === 401) {
      // Don't auto-redirect when the failing call is itself an auth endpoint —
      // that is a credentials error, not an expired session.
      const url: string = error.config?.url || '';
      const isAuthEndpoint = /\/auth\/(login|register|activate)/.test(url);

      if (!isAuthEndpoint) {
        localStorage.removeItem('access_token');
      }

      const cfg: unknown = error.config;
      const headers: unknown = error.config && typeof error.config === 'object'
        ? (error.config as Record<string, unknown>).headers
        : undefined;
      const skipAuthRedirect = Boolean(
        (cfg && typeof cfg === 'object' && (cfg as Record<string, unknown>)['skipAuthRedirect']) ||
        (headers && typeof headers === 'object' && (headers as Record<string, unknown>)['X-Skip-Auth-Redirect'])
      );
      if (typeof window !== 'undefined' && !skipAuthRedirect && !isAuthEndpoint) {
        window.location.href = localeHref('/auth/login');
      }
    }

    return Promise.reject(error);
  }
);

// Response interceptor #2 — circuit breaker (counts failures, closes
// on success). Registered after the 401 handler so it sees errors AFTER
// the auth redirect decision has been made.
apiClient.interceptors.response.use(onResponse, onResponseError);

// Request interceptor #1 — auth header. Registered first → runs second
// (LIFO). By that time the breaker has already approved the request.
/**
 * Thrown instead of sending a request while the marketing showcase is on
 * screen. Carries no `response`, so the 401 handling below never sees it and
 * cannot redirect.
 */
export class ShowcaseBlockedError extends Error {
  constructor(url?: string) {
    super(`Blocked under the showcase: ${url ?? 'request'}`);
    this.name = 'ShowcaseBlockedError';
  }
}

/**
 * Is this page the marketing showcase?
 *
 * Read from the PATH, not from Redux, for a reason worth writing down: the
 * showcase mounts its own store under a nested Provider, while the store this
 * module can reach (storeRef) is the APP's — where `ui.showcase` is false. The
 * route is the only signal that crosses that boundary.
 *
 * This is a BACKSTOP, not the mechanism. Each surface still declines its own
 * fetch (store/showcaseGate.ts, showcase/ShowcaseData.tsx) so it renders
 * fixtures rather than an error state. What this adds is the guarantee the
 * README claims — zero requests — for the surfaces nobody has gated yet, and,
 * more importantly, it stops a 401 from redirecting the browser to
 * /auth/login in the middle of a screenshot.
 */
function isShowcaseRoute(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.includes('/showcase/');
}

apiClient.interceptors.request.use(
  (config) => {
    if (isShowcaseRoute()) {
      throw new ShowcaseBlockedError(config.url);
    }
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Request interceptor #2 — circuit breaker. Registered last → runs
// first (LIFO). Short-circuits with CircuitOpenError when OPEN.
apiClient.interceptors.request.use(onRequest, (error) => Promise.reject(error));

export default apiClient;
